import React from "react";
import { useTranslation } from "react-i18next";
import Icon from "../../components/Icon";

const PAGE_SIZE = 25;

export default function AIActivityLog({ messages, footer, isLoading = false }) {
    const { t } = useTranslation();
    const bottomRef = React.useRef(null);
    const scrollRef = React.useRef(null);
    const [visibleCount, setVisibleCount] = React.useState(PAGE_SIZE);

    React.useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages.length, isLoading]);

    React.useEffect(() => { setVisibleCount(PAGE_SIZE); }, [messages.length]);

    const start = Math.max(0, messages.length - visibleCount);
    const visible = messages.slice(start);
    const hasMore = start > 0;

    const handleScroll = React.useCallback(() => {
        const el = scrollRef.current;
        if (el && el.scrollTop === 0 && hasMore) {
            const prevHeight = el.scrollHeight;
            setVisibleCount(v => Math.min(v + PAGE_SIZE, messages.length));
            requestAnimationFrame(() => { el.scrollTop = el.scrollHeight - prevHeight; });
        }
    }, [hasMore, messages.length]);

    return (
        <div className="flex flex-col h-full w-full bg-white">
            <div className="px-4 py-3 border-b flex items-center gap-2">
                <div className="w-7 h-7 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                    <Icon name="Sparkles" className="w-3.5 h-3.5 text-white" />
                </div>
                <div>
                    <p className="text-sm font-semibold text-gray-900">{t("builder.ai.title")}</p>
                    <p className="text-[10px] text-green-500 flex items-center gap-1">
                        <span className="w-1.5 h-1.5 bg-green-500 rounded-full inline-block" /> {t("builder.ai.active")}
                    </p>
                </div>
            </div>

            <div ref={scrollRef} onScroll={handleScroll} className="flex-1 overflow-y-auto p-4 space-y-3">
                {hasMore && <p className="text-center text-xs text-gray-400 py-1">{t("builder.ai.scrollUp")}</p>}
                {visible.map((msg, i) => (
                    <ChatBubble key={start + i} message={msg} isLatest={start + i === messages.length - 1} />
                ))}
                {isLoading && <TypingIndicator />}
                <div ref={bottomRef} />
            </div>
            {footer}
        </div>
    );
}

function TypingIndicator() {
    return (
        <div className="flex gap-2 animate-in fade-in slide-in-from-bottom-2 duration-300">
            <div className="w-6 h-6 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center shrink-0 mt-0.5">
                <Icon name="Sparkles" className="w-3 h-3 text-white" />
            </div>
            <div className="px-3 py-2.5 rounded-2xl rounded-bl-md bg-gray-100 flex items-center gap-1">
                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
            </div>
        </div>
    );
}

function ChatBubble({ message, isLatest }) {
    if (message.role === "user") {
        return (
            <div className="flex justify-end">
                <div className="bg-blue-600 text-white px-3 py-2 rounded-2xl rounded-br-md max-w-[85%] text-sm break-words overflow-hidden">
                    {message.text}
                </div>
            </div>
        );
    }

    return (
        <div className={`flex gap-2 ${isLatest ? "animate-in fade-in slide-in-from-bottom-2 duration-300" : ""}`}>
            <div className="w-6 h-6 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center shrink-0 mt-0.5">
                <Icon name="Sparkles" className="w-3 h-3 text-white" />
            </div>
            <div className={`px-3 py-2 rounded-2xl rounded-bl-md max-w-[85%] max-h-40 overflow-y-auto text-sm leading-relaxed break-words ${message.isError ? "bg-red-50 text-red-800 border border-red-200" : "bg-gray-100 text-gray-800"}`}>
                {message.typing && isLatest ? <TypingText text={message.text} /> : <FormattedText text={message.text} />}
            </div>
        </div>
    );
}

function TypingText({ text }) {
    const [displayed, setDisplayed] = React.useState("");
    React.useEffect(() => {
        let idx = 0;
        const interval = setInterval(() => {
            if (idx <= text.length) {
                setDisplayed(text.slice(0, idx));
                idx++;
            } else {
                clearInterval(interval);
            }
        }, 15);
        return () => clearInterval(interval);
    }, [text]);
    return <FormattedText text={displayed} />;
}

function FormattedText({ text }) {
    // Simple bold markdown: **text**
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return (
        <span>
            {parts.map((part, i) =>
                part.startsWith("**") && part.endsWith("**")
                    ? <strong key={i} className="font-semibold">{part.slice(2, -2)}</strong>
                    : part
            )}
        </span>
    );
}
